import {
	ClickAwayListener,
	Stack,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import { LineChart } from "@mui/x-charts";
import dayjs from "dayjs";
import { Info } from "lucide-react";
import { type FC, type ReactNode, useCallback, useMemo, useState } from "react";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DataLineChartButtons } from "web_myplann/Portfolio/Components/DataLineChart/DataLineChartButtons";
import { DataLineChartTooltip } from "web_myplann/Portfolio/Components/DataLineChart/DataLineChartTooltip";
import { SectionDivider } from "web_myplann/Portfolio/Components/SectionDidiver";
import type { DayData } from "web_myplann/Portfolio/Helpers/calculateGraphData";
import { PercentageDelta } from "web_myplann/Shared/Components/PercentageDelta";
import { useMouseTouchPosition } from "web_myplann/Shared/Hooks/useMouseTouchPosition";
import { moneyFormatter } from "web_myplann/Shared/Utils/moneyFormatters";
import { roundNumber } from "web_myplann/Shared/Utils/numberFormatters";
import {
	ARROW_DOWN_SIGN,
	ARROW_UP_SIGN,
	BILLIONS_SIGN,
	BLOOD_RED_MYPLANN,
	CIRCLE_SIGN,
	DANGER_MYPLANN_DARK,
	GRASS_GREEN_MYPLANN,
	MILLIONS_SIGN,
	SUCCESS_MYPLANN_DARK,
	THOUSANDS_SIGN,
} from "web_myplann/Utils/constants";
import type { ChartTimeFrameKey } from "web_myplann/Utils/types";

const defaultTimeFrame = 9999;
const paletteByMode = {
	dark: { up: SUCCESS_MYPLANN_DARK, down: DANGER_MYPLANN_DARK },
	light: { up: GRASS_GREEN_MYPLANN, down: BLOOD_RED_MYPLANN },
} as const;

type Props = {
	totalValue: number;
	totalEvaluationValue: number;
	totalEvaluationPercent: number;
	daysData: DayData[];
	currencySign: string;
	toDate: string;
	bottomComponent?: ReactNode;
	loanChart?: boolean;
	showChartInfo?: boolean;
	hideDivider?: boolean;
};

export const DataLineChart: FC<Props> = ({
	totalValue,
	totalEvaluationValue,
	totalEvaluationPercent,
	daysData,
	currencySign,
	toDate,
	bottomComponent,
	loanChart,
	showChartInfo,
	hideDivider,
}) => {
	const [timeFrame, setTimeFrame] =
		useState<ChartTimeFrameKey>(defaultTimeFrame);
	const [tooltipOpen, setTooltipOpen] = useState(false);

	const { t } = useResource();
	const theme = useTheme();

	const pos = useMouseTouchPosition();

	if (totalEvaluationPercent >= -0.01 && totalEvaluationPercent <= 0.01) {
		totalEvaluationPercent = 0;
	}

	const arrow = totalEvaluationValue > 0 ? ARROW_UP_SIGN : ARROW_DOWN_SIGN;

	const data = useMemo(
		() =>
			daysData.filter(
				(e) => !dayjs(e.date).isBefore(dayjs().subtract(timeFrame, "month")),
			),
		[daysData, timeFrame],
	);

	const { dates, values, invested, minDate, maxDate } = useMemo(() => {
		const dates = data?.map((e) => e.date);
		const values = data?.map((e) => roundNumber(e.value, 0));
		const invested = data?.map((e) => roundNumber(e.invested, 0));

		const minDate =
			dates.length > 0
				? new Date(new Date(dates[0]).getTime() - 14 * 60 * 60 * 1000)
				: undefined;
		const maxDate =
			dates.length > 0
				? new Date(
						new Date(dates[dates.length - 1]).getTime() + 12 * 60 * 60 * 1000,
					)
				: undefined;

		return { dates, values, invested, minDate, maxDate };
	}, [data]);

	const tickLabelStyle = useMemo(
		() => ({
			fontSize: 9,
			fontWeight: 400,
			fill: theme.palette.deprecated.grey[500],
		}),
		[theme],
	);

	const xValueFormatter = useCallback((date: Date) => {
		return `${dayjs(date).format("MM.YYYY")}`;
	}, []);

	const yValueFormatter = useCallback((val: number) => {
		if (val >= 1000000000) return `${val / 1000000000} ${BILLIONS_SIGN}`;
		if (val >= 1000000) return `${val / 1000000} ${MILLIONS_SIGN}`;

		return `${val / 1000} ${THOUSANDS_SIGN}`;
	}, []);

	const seriesValueFormatter = useCallback(
		(val: number | null) => moneyFormatter(val, currencySign),
		[currencySign],
	);

	const emptyValues = values.every((e) => e === 0);
	const emptyInvested = invested.every((e) => e === 0);

	if (daysData.length === 0 || (emptyValues && emptyInvested)) {
		return null;
	}

	const chartLabel = loanChart
		? t(Resources.Portfolio.Chart.RemainsToPay)
		: t(Resources.Portfolio.Chart.Value);

	const xLabel = loanChart
		? t(Resources.Portfolio.Chart.RemainsToPay)
		: t(Resources.Portfolio.Chart.Value);

	const yLabel = loanChart
		? t(Resources.Portfolio.Chart.Paid)
		: t(Resources.Portfolio.Chart.Invested);

	const arrowColor =
		paletteByMode[theme.palette.mode as keyof typeof paletteByMode][
			arrow === ARROW_UP_SIGN ? "up" : "down"
		];

	const isZeroInvestmentRatio = totalEvaluationPercent === 0;

	return (
		<>
			<Stack direction="row" gap={0.4} alignItems="center">
				<Typography color={theme.palette.deprecated.grey[500]}>
					{chartLabel}
				</Typography>
				<ClickAwayListener onClickAway={() => setTooltipOpen(false)}>
					<Tooltip
						PopperProps={{
							modifiers: [{ name: "offset", options: { offset: [80, -15] } }],
						}}
						open={tooltipOpen}
						disableTouchListener
						disableFocusListener
						disableHoverListener
						placement="top"
						title={t(Resources.Portfolio.Chart.Tooltip)}
						slotProps={{
							tooltip: {
								sx: {
									bgcolor: theme.palette.common.white,
									color: theme.palette.common.black,
									boxShadow: theme.shadows[3],
									borderRadius: theme.radii.md,
									fontWeight: 600,
									fontSize: 12,
								},
							},
						}}
					>
						<Info
							size={theme.iconSize.sm}
							color={theme.palette.deprecated.grey[700]}
							onClick={() => setTooltipOpen(!tooltipOpen)}
							style={{ cursor: "pointer" }}
						/>
					</Tooltip>
				</ClickAwayListener>
			</Stack>

			<Typography
				color={theme.palette.deprecated.grey[700]}
				fontSize={32}
				fontWeight={700}
			>
				{moneyFormatter(totalValue, currencySign, "", 2)}
			</Typography>

			{!loanChart && showChartInfo && (
				<Stack
					direction={"row"}
					columnGap={0.8}
					flexWrap="wrap"
					alignItems="center"
				>
					<Typography
						color={
							isZeroInvestmentRatio
								? theme.palette.deprecated.grey[500]
								: arrowColor
						}
						fontWeight={600}
						whiteSpace="nowrap"
					>
						{!isZeroInvestmentRatio && arrow}{" "}
						{moneyFormatter(
							totalEvaluationValue,
							currencySign,
							`0 ${currencySign}`,
							2,
						)}
					</Typography>
					<PercentageDelta
						change={totalEvaluationPercent}
						textBefore="("
						textAfter=")"
					/>
					<Stack direction="row" columnGap={0.8} alignItems="center">
						<Typography>{CIRCLE_SIGN}</Typography>
						<Typography
							color={theme.palette.deprecated.grey[500]}
							fontWeight={500}
							whiteSpace="nowrap"
						>
							{toDate}
						</Typography>
					</Stack>
				</Stack>
			)}

			{loanChart && (
				<Typography color={theme.palette.deprecated.grey[500]} fontWeight={500}>
					{t(Resources.Portfolio.Chart.To)} {toDate}
				</Typography>
			)}

			{dates && values && invested && (
				<LineChart
					xAxis={[
						{
							scaleType: "time",
							data: dates,
							disableTicks: true,
							valueFormatter: xValueFormatter,
							tickLabelStyle,
							min: minDate,
							max: maxDate,
						},
					]}
					yAxis={[
						{
							position: "right",
							disableLine: true,
							disableTicks: true,
							valueFormatter: yValueFormatter,
							tickLabelStyle,
						},
					]}
					series={[
						{
							data: invested,
							color: theme.palette.deprecated.grey[300],
							label: yLabel,
							showMark: false,
							disableHighlight: loanChart,
							valueFormatter: seriesValueFormatter,
						},
						{
							data: values,
							color: theme.palette.myPLANN.main,
							label: xLabel,
							showMark: false,
							valueFormatter: seriesValueFormatter,
						},
					]}
					height={250}
					margin={{ top: 20, right: -10, bottom: 0, left: 0 }}
					hideLegend
					skipAnimation
					slots={{ tooltip: () => <DataLineChartTooltip pos={pos} /> }}
					sx={{
						"& .MuiChartsAxisHighlight-root": {
							strokeDasharray: "none",
							stroke: theme.palette.deprecated.grey[500],
							strokeWidth: 0.5,
						},
						"& .MuiChartsAxis-line": {
							stroke: `${theme.palette.deprecated.grey[500]} !important`,
							strokeWidth: 0.5,
						},
						"& .MuiLineElement-series-auto-generated-id-0": {
							strokeWidth: loanChart ? 0 : 2,
						},
						"& .MuiLineElement-series-auto-generated-id-1": {
							strokeWidth: 4,
						},
					}}
				/>
			)}

			<DataLineChartButtons timeFrame={timeFrame} setTimeFrame={setTimeFrame} />
			<Stack pt={0.8}>{bottomComponent}</Stack>
			{!hideDivider && <SectionDivider />}
		</>
	);
};
